import { Dispatch, useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  attemptShowErrorsInSnackbar,
  useSnackbarActionContext,
} from "../../../context/SnackbarContext";
import { Input } from "../../sharedComponents/inputs/Input";
import { Sidebar, BaseButton } from "@velocity-ehs/component-library";
import { SnackbarType } from "../../sharedComponents/Snackbar";
import {
  getHierarchies,
  updateHierarchy,
} from "../../supportFunctions/graphql/HierarchiesQueries";
import { Hierarchy } from "./Hierarchies";
import { useTranslation } from "react-i18next";
import UsersService from "@velocity-ehs/user-utils-module";
import GatewayService from "../../../services/GatewayService";

type EditHierarchySliderProps = {
  hierarchy?: Hierarchy;
  showSlider: boolean;
  closeSliderCallback: () => void;
  onUpdate: Dispatch<any>;
  setOptions: Dispatch<any>;
};

const EditHierarchySlider = ({
  showSlider,
  closeSliderCallback,
  hierarchy,
  onUpdate,
  setOptions,
}: EditHierarchySliderProps): React.ReactElement => {
  const { t } = useTranslation();

  const { showSnackbar } = useSnackbarActionContext();

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<{ name: string }>({
    mode: "onBlur",
  });

  useEffect(() => {
    reset(hierarchy);
  }, [reset, hierarchy]);

  async function getHierarchyOptions() {
    try {
      if (UsersService.customer) {
        const response = await GatewayService.api.post(
          "",
          getHierarchies({ and: { customerId: UsersService.customer.id } })
        );
        setOptions(response.data.data.organizationalHierarchies.items);
      }
    } catch (error) {
      attemptShowErrorsInSnackbar(showSnackbar, error);
    }
  }

  const onSubmit = async (data: { name: string }) => {
    try {
      if (hierarchy) {
        const response = await GatewayService.api.post(
          "",
          updateHierarchy(hierarchy.id, data.name)
        );
        if (showSnackbar) {
          showSnackbar(
            SnackbarType.Success,
            `${hierarchy.name} was successfully renamed.`
          );
        }
        onUpdate((prev: any) => ({
          ...prev,
          name: response.data.data.updateOrganizationalHierarchy.name,
        }));
        await getHierarchyOptions();
        closeSliderCallback();
      }
    } catch (error) {
      attemptShowErrorsInSnackbar(showSnackbar, error);
    }
  };

  return (
    <>
      <Sidebar
        header={t("action_edit_hierarchy")}
        visible={showSlider}
        onHide={closeSliderCallback}
        footer={
          <BaseButton
            label={t("universal_Save")}
            type="submit"
            form="editHierarchyName"
            loading={isSubmitting}
          />
        }
      >
        <form id="editHierarchyName" onSubmit={handleSubmit(onSubmit)}>
          <Input
            control={control}
            name="name"
            label={t("universal_Name")}
            rules={{
              required: t("customer_Form_Name_Required"),
              maxLength: {
                value: 100,
                message: t("max_length_100_char_form_validation"),
              },
            }}
            errorMessage={errors ? errors.name?.message : undefined}
          />
        </form>
      </Sidebar>
    </>
  );
};

export default EditHierarchySlider;
